import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  GoogleFontIcon,
  Menu,
  MultiSelect,
  SingleSelect,
  Table,
} from '@asset-metrix/portal-ui';
import { Menu as MantineMenu, Text } from '@mantine/core';
import { skipToken, useQuery } from '@tanstack/react-query';

import { getInvestmentComplianceLimitPreview } from '../../api/portalGateway/getInvestmentComplianceLimitPreview';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';
import { extractErrorMessage } from '../../utils/errorMessage';
import { configToColDef } from '../table/configToColDef';

import {
  DataSourceConfiguration,
  InvestmentComplianceLimitDataLevel,
} from './createInvestmentLimitModel';
import {
  DataLevelOptions,
  previewSelection,
  toSelectOptions,
} from './investmentLimitTypes';
import { useInvestmentComplianceLimitOptions } from './useInvestmentComplianceLimitOptions';

import styles from './createInvestmentLimitModal.module.scss';

const PREVIEW_ROW_LIMIT = DEFAULT_PAGE_SIZE;

export type DataSourceSelectionProps = {
  contentId: string;
  dataLevels: InvestmentComplianceLimitDataLevel[];
  onChange: (update: Partial<DataSourceConfiguration>) => void;
  showSectionTitle?: boolean;
  source: DataSourceConfiguration;
};

export function DataSourceSelection({
  contentId,
  dataLevels,
  onChange,
  showSectionTitle = true,
  source,
}: DataSourceSelectionProps) {
  const { t } = useTranslation();
  const dataLevelIndex = dataLevels.findIndex(
    (level) => level.id === source.dataLevelId
  );
  const resolvedDataLevelIndex = dataLevelIndex === -1 ? null : dataLevelIndex;
  const hasDataLevel = resolvedDataLevelIndex != null;
  const selectedLevel =
    resolvedDataLevelIndex == null
      ? undefined
      : dataLevels[resolvedDataLevelIndex];
  const {
    data: optionsResponse,
    error: optionsError,
    isFetching: areOptionsLoading,
  } = useInvestmentComplianceLimitOptions(contentId, resolvedDataLevelIndex);
  const selectedLevelOptions: DataLevelOptions | undefined =
    optionsResponse?.data;
  const filterColumns = selectedLevel?.filters ?? [];
  const unusedFilterColumns = filterColumns.filter(
    (column) => !source.enabledFilterKeys.includes(column.key)
  );
  const currencyType = source.currencyType;
  const hasCurrencyType = currencyType != null && currencyType !== '';
  const currencyTypeKey = selectedLevel?.currencyTypeIdKey;
  const enabledFilterKeys = source.enabledFilterKeys;
  const filterValues = source.filterValues;
  const selection = useMemo(
    () =>
      previewSelection({
        currencyType,
        currencyTypeKey,
        enabledFilterKeys,
        filterValues,
      }),
    [currencyType, currencyTypeKey, enabledFilterKeys, filterValues]
  );
  const previewColumns = selectedLevel?.columns;
  const footerTitleColumnKey = previewColumns?.find(
    (column) => !column.hidden
  )?.key;

  const {
    data: preview,
    error: rowsError,
    isFetching,
  } = useQuery({
    queryKey: [
      'investment-compliance-limit-preview',
      contentId,
      resolvedDataLevelIndex,
      selection,
    ],
    queryFn:
      resolvedDataLevelIndex == null
        ? skipToken
        : () =>
            getInvestmentComplianceLimitPreview({
              contentId,
              dataLevelIndex: resolvedDataLevelIndex,
              limit: PREVIEW_ROW_LIMIT,
              offset: 0,
              selection,
            }),
    enabled: hasDataLevel && hasCurrencyType,
    placeholderData: (previousData) => previousData,
  });
  const rows = (preview?.data.items ?? []).slice(0, PREVIEW_ROW_LIMIT);
  const totalRows = preview?.data.meta.totalCount ?? 0;
  const remainingEntries =
    rows.length === PREVIEW_ROW_LIMIT && totalRows > rows.length
      ? totalRows - rows.length
      : 0;
  const columnDefs = useMemo(() => {
    const columns = previewColumns ?? [];
    return columns.map((column) =>
      configToColDef(column, contentId, false, columns, {
        footerTitleColumnKey,
      })
    );
  }, [contentId, footerTitleColumnKey, previewColumns]);
  const pinnedBottomRowData =
    footerTitleColumnKey != null && remainingEntries > 0
      ? [
          {
            [footerTitleColumnKey]: t('app.table.showMoreEntry', '', {
              count: remainingEntries,
            }),
          },
        ]
      : undefined;
  const loadError =
    rowsError != null ? extractErrorMessage(rowsError) : undefined;

  const addFilter = (key: string) => {
    if (source.enabledFilterKeys.includes(key)) {
      return;
    }
    onChange({
      enabledFilterKeys: [...source.enabledFilterKeys, key],
      filterValues: { ...source.filterValues, [key]: [] },
    });
  };

  const removeFilter = (key: string) => {
    const filterValues = { ...source.filterValues };
    delete filterValues[key];
    onChange({
      enabledFilterKeys: source.enabledFilterKeys.filter(
        (enabledKey) => enabledKey !== key
      ),
      filterValues,
    });
  };

  return (
    <div className={styles['parameter-section']}>
      {showSectionTitle && (
        <Text className={styles['section-title']}>
          {t('app.investmentLimit.preview.dataSource')}
        </Text>
      )}
      <div className={styles['form-grid']}>
        <SingleSelect
          clearable={false}
          data={dataLevels.map((level) => ({
            label: level.label,
            value: level.id,
          }))}
          label={t('app.investmentLimit.fields.dataLevel')}
          onChange={(value) =>
            onChange({
              currencyType: null,
              dataLevelId: value,
              enabledFilterKeys: [],
              filterValues: {},
            })
          }
          placeholder={t('app.investmentLimit.fields.selectLevel')}
          required
          value={source.dataLevelId}
        />
        <SingleSelect
          clearable={false}
          data={toSelectOptions(selectedLevelOptions?.currencyTypes)}
          disabled={!hasDataLevel || optionsError != null}
          label={t('app.investmentLimit.fields.currencyType')}
          loading={areOptionsLoading}
          onChange={(value) => onChange({ currencyType: value })}
          placeholder={t('app.investmentLimit.fields.selectCurrencyType')}
          required
          value={source.currencyType}
        />
      </div>
      {optionsError != null && (
        <Text className={styles['field-hint']}>
          {extractErrorMessage(optionsError)}
        </Text>
      )}
      <div className={styles['portfolio-filters']}>
        {source.enabledFilterKeys.map((key) => {
          const column = filterColumns.find((item) => item.key === key);
          return (
            <div className={styles['portfolio-filter-row']} key={key}>
              <div className={styles['portfolio-filter-field']}>
                <MultiSelect
                  data={toSelectOptions(
                    selectedLevelOptions?.filters.find(
                      (filter) => filter.key === key
                    )?.options
                  )}
                  label={column?.label ?? key}
                  onChange={(value) =>
                    onChange({
                      filterValues: { ...source.filterValues, [key]: value },
                    })
                  }
                  placeholder={t('app.investmentLimit.fields.selectNamed', {
                    label: column?.label ?? key,
                  })}
                  required
                  value={source.filterValues[key] ?? []}
                />
              </div>
              <Button
                icon={<GoogleFontIcon name="close" />}
                onClick={() => removeFilter(key)}
                title={t('app.investmentLimit.fields.removeFilter')}
                variant="secondary"
              />
            </div>
          );
        })}
        <Menu
          menuTarget={
            <Button
              disabled={
                selectedLevelOptions == null || unusedFilterColumns.length === 0
              }
              icon={<GoogleFontIcon name="add" />}
              loading={areOptionsLoading}
              variant="secondary"
            >
              {t('app.investmentLimit.preview.addFilter')}
            </Button>
          }
          menuDropdown={
            <>
              {unusedFilterColumns.map((column) => (
                <MantineMenu.Item
                  className={styles['compact-menu-item']}
                  key={column.key}
                  onClick={() => addFilter(column.key)}
                >
                  {column.label}
                </MantineMenu.Item>
              ))}
            </>
          }
        />
        {hasDataLevel && hasCurrencyType ? (
          <div className={styles['source-preview']}>
            <Text className={styles['field-hint']}>
              {loadError != null
                ? loadError
                : source.enabledFilterKeys.length === 0
                  ? t('app.investmentLimit.preview.allRecords')
                  : t('app.investmentLimit.preview.filteredRecords')}
            </Text>
            <Table
              columnDefs={columnDefs}
              expectedRowCount={PREVIEW_ROW_LIMIT}
              loading={isFetching}
              onSortingChanged={() => undefined}
              pinnedBottomRowData={pinnedBottomRowData}
              rowData={rows}
            />
          </div>
        ) : (
          <Text className={styles['source-preview-placeholder']}>
            {hasDataLevel
              ? t('app.investmentLimit.preview.currencyTypePreviewPlaceholder')
              : t('app.investmentLimit.preview.previewPlaceholder')}
          </Text>
        )}
      </div>
    </div>
  );
}
